import { DropdownOption, PayFrequency as LibPayFrequency } from '@aslanpay/portal-common-lib';
import {
  Payday,
  PayrollCutoff,
  PayType,
  PayFrequency,
  PayCalendarLocation,
  PayCalendarWorkingDay,
  startingBalanceDaysValues,
  odpAllowancePercentageValues,
  NonBusinessDayFailover,
  odpAllowancePercentagePercentageValues,
  globalAllowancePercentageValues,
  AccrualPeriodType,
  DeductionsConfigurations,
} from 'app/constants/payCalendar';

// setup paydayOptions dropdown
export const getPaydayOptions = (payFrequency?: LibPayFrequency): DropdownOption[] => {
  if (!payFrequency) return []; // or return a default set of options
  return Object.keys(Payday).reduce((acc: DropdownOption[], value) => {
    if (
      payFrequency !== 'MONTHLY' &&
      (value === 'LAST_DAY_OF_PERIOD' ||
        value === 'LAST_BUSINESS_DAY' ||
        value === 'N_DAY_OF_PERIOD')
    ) {
      acc.push({
        value,
        text: Payday[value as keyof typeof Payday],
      });
      return acc;
    }
    if (payFrequency === 'MONTHLY') {
      if (
        value === 'FIRST_BUSINESS_DAY' ||
        value === 'FIRST_CUSTOM_WEEKDAY' ||
        value === 'LAST_DAY_OF_MONTH'
      ) {
        return acc;
      }
      acc.push({
        value,
        text: Payday[value as keyof typeof Payday],
      });
      return acc;
    }
    return acc;
  }, []);
};

// setup paydayOptions dropdown
export const getCutOffOptions = (payFrequency?: LibPayFrequency): DropdownOption[] => {
  if (!payFrequency) return []; // or return a default set of options
  return Object.keys(PayrollCutoff).reduce((acc: DropdownOption[], value) => {
    if (
      payFrequency !== 'MONTHLY' &&
      (value === 'N_DAYS_BEFORE_PAYDAY' ||
        value === 'N_BUSINESS_DAYS_BEFORE_PAYDAY' ||
        value === 'N_DAY_OF_PERIOD')
    ) {
      acc.push({
        value,
        text: PayrollCutoff[value as keyof typeof PayrollCutoff],
      });
      return acc;
    }
    if (payFrequency === 'MONTHLY') {
      if (value === 'CUSTOM_DAY' || value === 'CUSTOM_DAY_IN_ARREARS') {
        return acc;
      }
      acc.push({
        value,
        text: PayrollCutoff[value as keyof typeof PayrollCutoff],
      });
      return acc;
    }
    return acc;
  }, []);
};

export function getPayFrequencyOptions(): DropdownOption[] {
  return Object.keys(PayFrequency).map((key) => ({
    value: key,
    text: PayFrequency[key as keyof typeof PayFrequency],
  }));
}

export function getPayTypeOptions(): DropdownOption[] {
  return Object.keys(PayType).map((key) => ({
    value: key,
    text: PayType[key as keyof typeof PayType],
  }));
}

export function getLocationOptions(): DropdownOption[] {
  return Object.keys(PayCalendarLocation).map((key) => ({
    value: key,
    text: PayCalendarLocation[key as keyof typeof PayCalendarLocation],
  }));
}

export function getWorkingDaysOptions(): DropdownOption[] {
  return Object.keys(PayCalendarWorkingDay).map((key) => ({
    value: key,
    text: PayCalendarWorkingDay[key as keyof typeof PayCalendarWorkingDay],
  }));
}

export function getOdpAllowancePercentageOptions(): DropdownOption[] {
  return odpAllowancePercentageValues.map((value) => ({
    value,
    text: `${value} %`,
  }));
}

export function getOdpAllowancePercentagePercentageOptions(): DropdownOption[] {
  return odpAllowancePercentagePercentageValues.map(({ label, percentage }) => ({
    value: percentage ?? 5,
    text: label ?? '5%',
  }));
}

export function getGlobalAllowancePercentageOptions(): DropdownOption[] {
  return globalAllowancePercentageValues.map(({ label, percentage }) => ({
    value: percentage ?? 5,
    text: label ?? '5%',
  }));
}

export function getStartingBalanceDaysOptions(
  payFrequency: 'WEEKLY' | 'FORTNIGHTLY' | 'FOUR_WEEKLY' | 'MONTHLY'
): DropdownOption[] {
  return startingBalanceDaysValues
    .filter(({ balance }) => {
      // Ensure balance is defined before filtering
      if (balance === undefined) {
        return false;
      }
      switch (payFrequency) {
        case 'WEEKLY':
          return (balance >= 0 && balance <= 5) || balance === 100;
        case 'FORTNIGHTLY':
          return (balance >= 0 && balance <= 10) || balance === 100;
        case 'FOUR_WEEKLY':
          return (balance >= 0 && balance <= 20) || balance === 100;
        case 'MONTHLY':
          return (balance >= 0 && balance <= 19) || balance === 100;
        default:
          return true;
      }
    })
    .map(({ label = '', balance = 0 }) => ({
      value: balance,
      text: `${label}`,
    }));
}

export function getNonBusinessDayFailoverOptions(): DropdownOption[] {
  return Object.keys(NonBusinessDayFailover).map((key) => ({
    value: key,
    text: NonBusinessDayFailover[key as keyof typeof NonBusinessDayFailover],
  }));
}

export function getAccrualPeriodOptions(): DropdownOption[] {
  return Object.keys(AccrualPeriodType).map((key) => ({
    value: key,
    text: AccrualPeriodType[key as keyof typeof AccrualPeriodType],
  }));
}

export function getDeductionsConfigOptions(
  options: { exclude?: (keyof typeof DeductionsConfigurations)[] } = {}
): DropdownOption[] {
  const { exclude = [] } = options;
  return Object.keys(DeductionsConfigurations)
    .filter((key) => !exclude.includes(key as keyof typeof DeductionsConfigurations))
    .map((key) => ({
      value: key,
      text: DeductionsConfigurations[key as keyof typeof DeductionsConfigurations],
    }));
}
